import {Tr} from "@/i18n/i18n";
import {injectable} from "inversify";
import {PlayerMarker, Prompt} from "@/store/player/types";
import RoadLesson from "@/lesson.ui/roadLesson";
import Scene = Phaser.Scene;
import EducationPanel from "@/scenes.ui/education-panel";
import {Keys} from "@/constants/key";
import TextButton from "@/scenes.ui/text-button";
import {
    ArtifactPlace,
    ConsumablePlace,
    CustomPlace, ExchangePlace, GainablePlace, MagicPlace,
    Place,
    PlaceVisitor, PointerPlace, PortalPlace,
    ResourcePlace,
    ResourceType, RunePlace, SkillPlace, TalkerPlace, TurnPlace
} from "@/store/place/types";
import {Point} from "@/model/point";
import Image = Phaser.GameObjects.Image;
import {Markers} from "@/constants/marker";
import {Creature} from "@/store/level/types";
import {CreatureUtils} from "@/utils/creature-utils";
import {MenuService} from "@/services/menu-service";
import {PlayerCreatureService} from "@/services/player-creature-service";
import PlayerStore from "@/store/player";
import PlaceStore from "@/store/place";
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";

@injectable()
export class OldRoadLessonService {

    private roadLesson!: RoadLesson | undefined;

    private educationPanel: EducationPanel | undefined;
    private mouseEnabledCallback: ((enabled: boolean) => unknown) | undefined;
    private stopCallback: (() => unknown) | undefined;
    private movingAllowedCallback: ((allowed: boolean) => unknown) | undefined;

    private educationActive: boolean = true;

    private scene: Scene | undefined;
    private canvas: HTMLCanvasElement | undefined;
    private nearestGoldPoint: Point | undefined;
    private nearestCrystalPoint: Point | undefined;
    private nearestChestPoint: Point | undefined;

    private flyingArrow: Image | undefined;

    private positionY: number | undefined;

    private buttonClickSound: Phaser.Sound.BaseSound | undefined;

    private okButton: TextButton | undefined;

    private onLessonShowingCallback!: (isLessonShowing: boolean) => unknown

    private menuService!: MenuService;
    private creatureService!: PlayerCreatureService;

    public showRoadLesson(scene: Scene, canvas: HTMLCanvasElement, positionY: number, buttonClickSound: (Phaser.Sound.BaseSound | undefined),
                          mouseEnabledCallback: (enabled: boolean) => unknown, stopCallback: () => unknown,
                          movingAllowedCallback: (allowed: boolean) => unknown, onLessonShowingCallback: (isLessonShowing: boolean) => unknown,
                          menuService: MenuService, creatureService: PlayerCreatureService) {
        this.scene = scene;
        this.canvas = canvas;
        this.mouseEnabledCallback = mouseEnabledCallback;
        this.stopCallback = stopCallback;
        this.movingAllowedCallback = movingAllowedCallback;
        this.onLessonShowingCallback = onLessonShowingCallback;
        this.buttonClickSound = buttonClickSound;
        this.positionY = positionY;
        this.menuService = menuService;
        this.creatureService = creatureService;

        this.destroyContainers();
        this.doTutorial(scene);
    }

    private doTutorial(scene: Scene): void {

        if (this.haveMarker(Markers.GO_TO_WHITE_RABBIT_MARKER)) {

            this.destroyContainers()
            this.mouseEnabledCallback!(true);
        }

        if (this.haveMarker(Markers.ROAD_EDU_COMPLETED_MARKER)) {
            this.showPrompt();
            this.educationActive = false;
            return;
        }

        if (!this.haveMarker(Markers.INTRO_1_MARKER)) {

            this.mouseEnabledCallback!(false);
            this.showReplicOnly(scene, Markers.INTRO_1_MARKER, Tr('privet.ja.cheshirskiy.kot.i.tvoy.verniy.kompanon.v.zazerkale'), Tr('dalshe'));

        } else if (!this.haveMarker(Markers.INTRO_2_MARKER)) {

            this.mouseEnabledCallback!(false);
            this.showReplicOnly(scene, Markers.INTRO_2_MARKER, Tr('zazerkale.mir.geroev.i.volshebnikov.monstrov.i.tayn.') +
                Tr('nam.predstoit.issledovat.etot.mir'), Tr('dalshe'));

        } else if (!this.haveMarker(Markers.INTRO_3_MARKER)) {

            this.mouseEnabledCallback!(false);
            this.showReplicOnly(scene, Markers.INTRO_3_MARKER, Tr('a.poka.davay.ne.budem.terjat.ni.minuti'), Tr('vpered'));

        } else if (!this.haveMarker(Markers.ROAD_FINGER_MARKER)) {

            if (!this.roadLesson) {
                this.destroyContainers();
                this.mouseEnabledCallback!(true);
                this.roadLesson = new RoadLesson(scene, this.canvas!.width / 2, 300);
                this.roadLesson.showLesson();
                scene.add.existing(this.roadLesson);
                scene.input.on('pointerdown', () => {
                    if (!this.haveMarker(Markers.ROAD_FINGER_MARKER)) {
                        PlayerStore.setMarker(Markers.ROAD_FINGER_MARKER);
                        if (this.roadLesson) {
                            this.roadLesson.destroyLesson()
                        }
                    }
                })
            }

        } else if (this.haveMarker(Markers.GOLD_MARKER) && this.haveMarker(Markers.CRYSTAL_MARKER)
            && !this.haveMarker(Markers.RESOURCES_GOT_MARKER)) {

            this.destroyContainers();
            this.showResourceGotReplic();

        } else if (this.haveMarker(Markers.RESOURCES_GOT_MARKER) && !this.haveMarker(Markers.VICTORY_FACT_MARKER)) {

            this.destroyContainers()
            this.mouseEnabledCallback!(true);

        } else if (this.haveMarker(Markers.VICTORY_FACT_MARKER) && !this.haveMarker(Markers.GO_TO_WHITE_RABBIT_MARKER)) {

            this.mouseEnabledCallback!(false);
            this.movingAllowedCallback!(true);
            this.showReplicOnly(scene, Markers.GO_TO_WHITE_RABBIT_MARKER, Tr('nedavno.ja.poluchil.poslanie.ot.belogo.krolika'), Tr('vpered'), false);

        } else if (this.haveMarker(Markers.ART_TAKEN_MARKER) && !this.haveMarker(Markers.HERO_VISITED_AFTER_PROMPT_MARKER)) {

            let creatureWithArtifact: Creature | undefined = CreatureUtils.findFirstHavingArtifact(PlayerStore.getPlayer());

            if (!!creatureWithArtifact) {
                let heroCardPoint = this.menuService.getCreaturePanelPosition(creatureWithArtifact.id);

                if (heroCardPoint) {

                    this.showReplicOnPoint(heroCardPoint, Markers.HERO_VISITED_AFTER_PROMPT_MARKER,
                        Tr('otlichno.teper.klikni.na.geroja.'), 0, 1500, true);
                    this.mouseEnabledCallback!(false);
                }
            }
        }

    }

    private destroyContainers() {
        if (this.educationPanel) {
            this.educationPanel.destroy();
            this.educationPanel = undefined;
        }
        if (this.flyingArrow) {
            this.flyingArrow.destroy();
            this.flyingArrow = undefined;
        }

    }

    private async cancelEducation() {

        if (this.onLessonShowingCallback != null) {
            this.onLessonShowingCallback(false);
        }

        if (!this.haveMarker(Markers.INTRO_3_MARKER)) {

            await PlayerStore.setMarkers([
                Markers.INTRO_1_MARKER,
                Markers.INTRO_2_MARKER,
                Markers.INTRO_3_MARKER]);

        } else if (!this.haveMarker(Markers.RESOURCES_GOT_MARKER)) {

            await PlayerStore.setMarkers([
                Markers.ROAD_FINGER_MARKER,
                Markers.GOLD_MARKER,
                Markers.CRYSTAL_MARKER,
                Markers.RESOURCES_GOT_MARKER]);

        } else if (!this.haveMarker(Markers.VICTORY_FACT_MARKER)) {

            this.destroyContainers();
            await PlayerStore.setMarkers([Markers.VICTORY_FACT_MARKER]);
            const self = this;
            this.scene!.time.delayedCall(300, () => {
                self.doTutorial(self.scene!);
            });
            return;

        } else if (!this.haveMarker(Markers.ART_TAKEN_MARKER)) {

            await PlayerStore.setMarkers([Markers.ART_TAKEN_MARKER, Markers.HERO_VISITED_AFTER_PROMPT_MARKER]);

        } else if (this.haveMarker(Markers.ART_EDU_COMPLETED_MARKER) && !this.haveMarker(Markers.ROAD_EDU_COMPLETED_MARKER)) {

            await PlayerStore.setMarkers([Markers.ROAD_EDU_COMPLETED_MARKER]);
        }

        if (this.okButton) {
            this.okButton.destroy();
        }
        this.destroyContainers();
        this.mouseEnabledCallback!(true);
        this.movingAllowedCallback!(true);
    }

    private showReplicOnly(scene: Scene, marker: string, text: string, buttonText: string, cancellable: boolean = true): void {
        if (!this.educationPanel) {
            this.educationPanel = new EducationPanel(scene, 0, cancellable ? () => this.cancelEducation() : undefined);
            scene.add.existing(this.educationPanel);
        }
        this.stopCallback!();
        this.educationPanel!.init(PlayerStore.getPlayer().companionImage, text, () => {
            this.showOkButton(scene, buttonText, marker);
        }, () => {
            this.switchTutorial(scene, marker)
        });

        if (this.onLessonShowingCallback != null) {
            this.onLessonShowingCallback(true);
        }
    }

    private showReplicOnPlace(placePoint: Point, forbiddenMarker: string, text: string, minPosY: number, arrowDelay: number, markerToSet?:string): boolean {
        if (placePoint) {
            let posY = this.canvas!.height - (placePoint!.y - this.positionY!) * CU.S;
            let res = this.showReplicOnPoint(new Point(placePoint.x * CU.S, posY), forbiddenMarker, text, minPosY, arrowDelay, false);
            if (res && markerToSet) {
                PlayerStore.setMarker(markerToSet);
            }
            return res;
        }
        return false;
    }

    private showReplicOnPoint(point: Point, forbiddenMarker: string, text: string, minPosY: number, arrowDelay: number, heroesArrow: boolean): boolean {
        if (point != null) {

            if (point.y > minPosY && point.y < (this.canvas!.height - 30) && !this.haveMarker(forbiddenMarker)) {
                this.showArrow(point, arrowDelay, heroesArrow);

                this.stopCallback!();
                this.movingAllowedCallback!(false);
                if (!this.educationPanel) {
                    this.educationPanel = new EducationPanel(this.scene!, 0, () => this.cancelEducation());
                    this.scene!.add.existing(this.educationPanel!);
                }

                this.educationPanel!.init(PlayerStore.getPlayer().companionImage, text,  () => {
                    if (this.flyingArrow) { this.flyingArrow.alpha = 1; }
                });

                if (this.onLessonShowingCallback != null) {
                    this.onLessonShowingCallback(true);
                }

                return true;
            }
        }
        return false;
    }

    public updatePosition(positionY: number): void {
        if (this.educationActive) {
            this.positionY = positionY;
            if (this.educationPanel && this.educationPanel.isAnimating()) {
                this.educationPanel.completeText();
            } else {
                this.destroyContainers();
                let minPosY = 330 * CU.S;
                this.showReplicOnPlace(this.nearestGoldPoint!, Markers.GOLD_MARKER, Tr('smotri.mi.nashli.zoloto'), minPosY, 500);
                if (this.haveMarker(Markers.GOLD_MARKER)) {
                    this.showReplicOnPlace(this.nearestCrystalPoint!, Markers.CRYSTAL_MARKER, Tr('otlichno.ah.chut.ne.propustil.eta.kuchka.s.kristallami.nam.tozhe.nuzhna'), minPosY, 500);
                }
                this.showReplicOnPlace(this.nearestChestPoint!, Markers.VICTORY_FACT_MARKER, Tr('vot.bi.posmotret.chto.v.etom.sunduke'), minPosY, 500,
                    Markers.CHEST_BY_PLANTS_MARKER);
            }
        }
    }

    public onPlacesLoaded(positionY: number) {
        if (this.educationActive) {
            if (this.haveMarker(Markers.ART_EDU_COMPLETED_MARKER) && !this.haveMarker(Markers.ROAD_EDU_COMPLETED_MARKER)) {
                this.positionY = positionY;

                let p: Place = PlaceStore.getBattleLostPlace()!;
                if (p && p.creatureId) {
                    this.showReplicOnPlace(p.getPosition(), Markers.ROAD_EDU_COMPLETED_MARKER,
                        Tr('a.teper.vozmem.revansh.dumaju.teper.to.mi.pobedim.etih.monstrov.'), 0, 1500);
                    PlayerStore.SET_MARKER(Markers.ROAD_EDU_COMPLETED_MARKER);
                    PlayerStore.setMarker(Markers.ROAD_EDU_COMPLETED_MARKER);
                }
            }
        }
    }

    public addPlace(place: Place, positionY: number) {
        if (this.educationActive) {
            let self = this;
            self.positionY = positionY;
            place.accept(new class implements PlaceVisitor {

                visitArtifactPlace(p: ArtifactPlace): void {
                    if (p.special) {

                        self.showReplicOnPlace(p.getPosition(), Markers.ART_TAKEN_MARKER,
                            Tr('proigrali.nichego.chtobi.luchshe.srazhatsja.davay.podberem.etot.schit'), 300, 2000);
                    }
                }

                visitCustomPlace(p: CustomPlace): void {
                    if (p.placeId === 'chest1-c') {
                        if (!self.nearestChestPoint || p.posY < self.nearestChestPoint.y) {
                            self.nearestChestPoint = p.getPosition();
                        }
                        if (!p.creatureId) {
                            if (!self.haveMarker(Markers.VICTORY_FACT_MARKER)) {
                                PlayerStore.setMarker(Markers.VICTORY_FACT_MARKER).then(() => {
                                    self.doTutorial(self.scene!);
                                });
                            }
                        }
                    }
                }

                visitResourcePlace(p: ResourcePlace): void {
                    if (p.resourceType == ResourceType.GOLD) {
                        if (!self.nearestGoldPoint || p.posY < self.nearestGoldPoint.y) {
                            self.nearestGoldPoint = p.getPosition();
                        }
                    } else if (p.resourceType == ResourceType.CRYSTAL) {
                        if (!self.nearestCrystalPoint || p.posY < self.nearestCrystalPoint.y) {
                            self.nearestCrystalPoint = p.getPosition();
                        }
                    }
                }

                visitConsumablePlace(p: ConsumablePlace): void {}
                visitPointerPlace(place: PointerPlace): void {}
                visitTurnPlace(place: TurnPlace): void {}
                visitRunePlace(p: RunePlace): void {}
                visitGainablePlace(place: GainablePlace): void {}
                visitMagicPlace(place: MagicPlace): void {}
                visitExchangePlace(place: ExchangePlace): void {}
                visitPortalPlace(place: PortalPlace): void {}
                visitSkillPlace(place: SkillPlace): void {}
                visitTalkerPlace(place: TalkerPlace): void {}
            });
        }
    }

    public onTakeRune() {
        this.showPrompt();
    }

    private showPrompt() {
        let prompt: Prompt | undefined = PlayerStore.getPrompt();
        if (prompt) {
            let point = this.menuService.getCreaturePanelPosition(prompt.creatureId);

            if (point) {
                this.creatureService.setArtifactIdToOpen(prompt.artifactId);
                this.showReplicOnPoint(point, 'non_existing_marker', prompt.text, 0, 1500, true);
                this.mouseEnabledCallback!(false);
            }

            PlayerStore.RESET_PROMPT();
        }
    }

    public onTakeArtifact(p: ArtifactPlace) {
        if (this.educationActive) {
            if (!this.haveMarker(Markers.ART_TAKEN_MARKER) && p.special) {
                if (this.educationPanel) {
                    this.educationPanel.removeText();
                }
                if (this.flyingArrow) {
                    this.flyingArrow.destroy();
                    this.flyingArrow = undefined;
                }
                PlayerStore.setMarker(Markers.ART_TAKEN_MARKER).then(() => {
                    this.doTutorial(this.scene!);
                });
            }
        }
    }

    public onTakeResource(p: ResourcePlace) {
        if (this.educationActive) {
            if (!this.haveMarker(Markers.GOLD_MARKER) || !this.haveMarker(Markers.CRYSTAL_MARKER)) {
                if (this.educationPanel) {
                    this.educationPanel.removeText();
                }
                if (this.flyingArrow) {
                    this.flyingArrow.destroy();
                    this.flyingArrow = undefined;
                }
                if (p.resourceType === ResourceType.GOLD) {
                    if (!this.haveMarker(Markers.GOLD_MARKER)) {
                        PlayerStore.setMarker(Markers.GOLD_MARKER).then(() => {
                            this.showReplicOnPlace(this.nearestCrystalPoint!, Markers.CRYSTAL_MARKER, Tr('otlichno.ah.chut.ne.propustil.eta.kuchka.s.kristallami.nam.tozhe.nuzhna'), 290, 3500);
                            this.showResourceGotReplic();
                        });
                    }
                } else if (p.resourceType === ResourceType.CRYSTAL) {
                    if (!this.haveMarker(Markers.CRYSTAL_MARKER)) {
                        PlayerStore.setMarker(Markers.CRYSTAL_MARKER).then(() => {
                            this.showReplicOnPlace(this.nearestGoldPoint!, Markers.GOLD_MARKER, Tr('smotri.mi.nashli.zoloto'), 310, 500);
                            this.showResourceGotReplic();
                        });
                    }
                }
            }
        }
    }

    private showResourceGotReplic() {
        if (this.haveMarker(Markers.GOLD_MARKER) && this.haveMarker(Markers.CRYSTAL_MARKER)) {
            this.mouseEnabledCallback!(false);
            this.movingAllowedCallback!(true);
            this.showReplicOnly(this.scene!, Markers.RESOURCES_GOT_MARKER, Tr('mi.nashli.i.sobrali.resursi.eto.ochen.horosho'), Tr('otlichno'));
        }
    }

    private haveMarker(marker: String): Boolean {
        return PlayerStore.getPlayer().playerMarkers.some((m: PlayerMarker) => m.playerMarker == marker);
    }

    private showOkButton(scene: Scene, buttonText: string, clickMarker: string) {
        this.okButton = new TextButton(scene, 410, 210, Keys.GREEN_HORIZONTAL_BUTTON, buttonText);
        this.okButton.setDepth(1001);
        scene.add.existing(this.okButton);
        this.okButton.alpha = 0;

        scene.tweens.chain({
            targets: this.okButton,
            tweens: [
                {},
                {
                    duration: 400,
                    alpha: 1
                }]
        });

        this.okButton.setCallback(() => {
            this.switchTutorial(scene, clickMarker);
        });
    }

    public switchTutorial(scene: Scene, clickMarker: string){
        SoundUtils.play(this.buttonClickSound);
        PlayerStore.setMarker(clickMarker).then(() => {
            if (this.educationPanel) {
                this.educationPanel.removeText();
            }
            if (this.okButton) {
                this.okButton.destroy();
            }
            if (this.onLessonShowingCallback != null) {
                this.onLessonShowingCallback(false);
            }
            this.doTutorial(scene);
        });
    }

    private showArrow(point: Point, arrowDelay: number, flipX: boolean) {
        if (this.flyingArrow) {
            this.flyingArrow.setVisible(false)
            this.flyingArrow.destroy();
        }
        this.flyingArrow = SpriteUtils.addSprite(this.scene!, point.x + 10, point.y - 10, Keys.ATLAS_UI, Keys.ARROW_CROSS)
            .setOrigin(0, 1);
        this.flyingArrow.flipX = flipX;
        this.flyingArrow.setDepth(2000);
        if (flipX) {
            this.flyingArrow.rotation = 0.3;
        }
        this.flyingArrow.alpha = 0;

        this.scene!.tweens.chain({
            targets: this.flyingArrow,
            tweens: [{},
                {
                    delay: arrowDelay,
                    duration: 800,
                    alpha: 1
                }]
        });
        this.scene!.tweens.add({
            targets: this.flyingArrow,
            delay: 0,
            duration: 2000,
            x: this.flyingArrow.x / CU.S + (flipX ? -25 : 20),
            y: this.flyingArrow.y / CU.S - (!flipX ? 25 : 30),
            yoyo: true,
            loop: -1,
        });
        this.flyingArrow.setPosition(point.x + 10, point.y - 10);
    }

    public showRoadHand(scene: Scene) {
        if (!this.roadLesson) {
            this.destroyContainers();
            this.mouseEnabledCallback!(true);
            this.roadLesson = new RoadLesson(scene, this.canvas!.width / 2, 300);
            this.roadLesson.showLesson();
            scene.add.existing(this.roadLesson);
            scene.input.on('pointerdown', () => {
                if (!this.haveMarker(Markers.ROAD_FINGER_MARKER)) {
                    PlayerStore.setMarker(Markers.ROAD_FINGER_MARKER);
                }
                if (this.roadLesson) {
                    this.roadLesson.destroyLesson()
                    this.roadLesson = undefined;
                }
            })
        }
    }
}
